/**
 * Recently opened campaigns / templates / views for the New Tab page.
 * One list per workspace, on this computer only.
 */

import { getActiveWorkspaceId, PERSONAL_WORKSPACE_ID } from './api/workspaceContext';

const LEGACY_KEY = 'fokus_recent_workspace_items';
const MAX_RECENT = 12;

const roomKey = (workspaceId) => {
  const id = workspaceId || getActiveWorkspaceId() || PERSONAL_WORKSPACE_ID;
  return `${LEGACY_KEY}:${id}`;
};

const safeParse = (raw) => {
  try {
    const v = JSON.parse(raw);
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
};

/** Move the old unscoped list into Personal once, so existing recents are not lost. */
function migrateLegacyIfNeeded() {
  if (typeof localStorage === 'undefined') return;
  try {
    const personalKey = `${LEGACY_KEY}:${PERSONAL_WORKSPACE_ID}`;
    if (localStorage.getItem(personalKey)) return;
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (!legacy) return;
    localStorage.setItem(personalKey, legacy);
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * @typedef {{ type: 'campaign'|'template'|'view', id: string, label: string, icon: string|null, openedAt: number }} RecentWorkspaceItem
 */

/** @returns {RecentWorkspaceItem[]} */
export function getRecentWorkspaceItems(workspaceId) {
  if (typeof localStorage === 'undefined') return [];
  migrateLegacyIfNeeded();
  return safeParse(localStorage.getItem(roomKey(workspaceId)));
}

/**
 * Record an opened workspace item (most recent first).
 * @param {{ type: 'campaign'|'template'|'view', id: string, label?: string, icon?: string|null }} item
 */
export function addRecentWorkspaceItem(item, workspaceId) {
  if (!item?.type || !item?.id) return;
  if (typeof localStorage === 'undefined') return;

  migrateLegacyIfNeeded();

  const next = {
    type: item.type,
    id: item.id,
    label: item.label || 'Untitled',
    icon: item.icon ?? null,
    openedAt: Date.now(),
  };

  const recent = getRecentWorkspaceItems(workspaceId).filter(
    (r) => !(r.type === next.type && r.id === next.id)
  );
  recent.unshift(next);

  try {
    localStorage.setItem(roomKey(workspaceId), JSON.stringify(recent.slice(0, MAX_RECENT)));
  } catch {
    // Ignore quota / private-mode failures
  }
}
